import { Link } from 'react-router-dom'

import { api } from '../api'
import { datum, tonnen, zeitpunkt } from '../format'
import { useAktion } from '../hooks'
import { gespraechPfad } from '../pfade'
import { FADENART, WAECHTERART } from '../texte'
import Meldungen from './Meldungen'
import type { OffenerFaden, Uebersicht, Waechter } from '../typen'

/** Was ein Wächter beobachtet, in einer Zeile: „930198 · unter 12,000 t" oder „am 15.11.2026". */
export function waechterWorauf(w: Waechter): string {
  return [
    w.artikelnummer && `Artikel ${w.artikelnummer}`,
    w.art === 'bestand_unter' && w.schwelle !== null && `unter ${tonnen(w.schwelle)} t`,
    w.art === 'termin' && w.datum && `am ${datum(w.datum)}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** Wohin ein Faden führt: der Link des Backends, sonst das Gespräch, sonst der Assistent. */
function fadenPfad(f: OffenerFaden): string {
  if (f.link) return f.link
  return f.unterhaltung_id !== null ? gespraechPfad(f.unterhaltung_id) : '/assistent'
}

/**
 * Was der Assistent für „Heute" hinterlässt (02.11.2026): Wächter, die angeschlagen haben („sag mir,
 * wenn 10713 in der Produktionsplanung auftaucht"), und Fäden, die in einem Gespräch liegen geblieben
 * sind - eine offene Frage, eine wartende Freigabe, ein abgebrochenes Gespräch. Vorher fragte Frau
 * Barth am nächsten Morgen: „Ist das jetzt schon erledigt?" Ohne beides steht hier nichts.
 */
export default function OffenesAusDemAssistenten({ uebersicht, onGeaendert }: { uebersicht: Uebersicht | null; onGeaendert: () => void }) {
  const waechter = uebersicht?.waechter_ausgeloest ?? []
  const faeden = uebersicht?.offene_faeden ?? []
  const { fehler, erfolg, arbeitet, ausfuehren } = useAktion()
  if (waechter.length === 0 && faeden.length === 0 && !fehler && !erfolg) return null

  function gesehen(w: Waechter) {
    void ausfuehren(async () => {
      await api.waechterGesehen(w.id)
      onGeaendert()
    }, 'Als gesehen vermerkt.')
  }

  return (
    <>
      <Meldungen fehler={fehler} erfolg={erfolg} />
      {waechter.length > 0 && (
        <div className="karte datenpruefung" id="waechter">
          <div className="leiste">
            <b>Wächter haben angeschlagen</b>
            <span className="hinweis">
              {waechter.length === 1 ? 'Ein Auftrag' : `${waechter.length} Aufträge`} „sag mir, wenn …“ - alle unter{' '}
              <Link to="/einstellungen#waechter">Einstellungen</Link>
            </span>
          </div>
          <ul className="befund-liste">
            {waechter.map((w) => (
              <li key={w.id} className="befund warnung">
                <span className="status warnung">{WAECHTERART[w.art] ?? w.art}</span>
                <div className="befund-text">
                  <b>{w.text}</b>
                  {waechterWorauf(w) && <span className="hinweis"> ({waechterWorauf(w)})</span>}
                  {w.ausloeser && <div>{w.ausloeser}</div>}
                  <div className="hinweis">
                    angeschlagen {w.ausgeloest_am ? `am ${datum(w.ausgeloest_am)}` : ''}
                    {w.angelegt_von ? ` · gewünscht von ${w.angelegt_von} am ${datum(w.angelegt_am)}` : ''}
                  </div>
                  <div className="befund-knoepfe">
                    <button disabled={arbeitet} onClick={() => gesehen(w)}>
                      Gesehen
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {faeden.length > 0 && (
        <div className="karte datenpruefung" id="offene-faeden">
          <div className="leiste">
            <b>Offen aus dem Assistenten</b>
            <span className="hinweis">Was in einem Gespräch liegen geblieben ist - mit dem Weg zurück dorthin.</span>
          </div>
          <ul className="befund-liste">
            {faeden.map((f, n) => (
              <li key={`${f.art}-${f.unterhaltung_id ?? 'x'}-${n}`} className="befund hinweis">
                <span className="status hinweis">{FADENART[f.art] ?? f.art}</span>
                <div className="befund-text">
                  <div>{f.text}</div>
                  <div className="hinweis">
                    seit {zeitpunkt(f.seit)}
                    {f.titel ? ` · Gespräch „${f.titel}“` : ''}
                  </div>
                  <Link className="hinweis-link" to={fadenPfad(f)}>
                    {f.unterhaltung_id !== null ? 'Gespräch öffnen' : 'öffnen'}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
